import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  authStatusQueryOptions,
  currentUserQueryOptions,
  logoutMutationOptions,
} from '../api/queries/auth';

export function useAuth() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const { data: authStatus, isLoading: isAuthStatusLoading } = useQuery(authStatusQueryOptions);

  // Getting current user query if authenticated
  const {
    data: userData,
    isLoading: isUserLoading,
    error: userError,
  } = useQuery({
    ...currentUserQueryOptions,
    enabled: authStatus?.authenticated ?? false,
  });

  // Handle user query errors by invalidating auth status
  // This handles the case where auth status says authenticated but user query fails.
  useEffect(() => {
    if (userError && axios.isAxiosError(userError) && userError.response?.status === 401) {
      queryClient.invalidateQueries({ queryKey: ['auth', 'status'] });
    }
  }, [userError, queryClient]);

  const login = (redirectTo?: string | null) => {
    const redirectUrl = redirectTo || window.location.pathname + window.location.search;
    const encodedRedirectUrl = encodeURIComponent(redirectUrl);
    window.location.href = `${import.meta.env.VITE_SPARK_API_BASE_URL}/oauth/salesforce/authorize?redirect_to=${encodedRedirectUrl}`;
  };

  const { mutate: logout } = useMutation({
    ...logoutMutationOptions,
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ['auth'] });
      toast.success('Logged out successfully');
      navigate('/', { replace: true });
    },
    onError: error => {
      console.error('Logout failed:', error);
      toast.error('Logout failed');
    },
  });

  // Computed values
  const isAuthenticated = authStatus?.authenticated ?? false;
  const hasRefreshToken = authStatus?.has_refresh_token ?? false;
  const user = userData?.user ?? null;
  const isLoading = isAuthStatusLoading || isUserLoading;

  const userLanguage = user?.language ?? null;
  useEffect(() => {
    if (userLanguage) {
      i18n.changeLanguage(userLanguage);
    }
  }, [userLanguage, i18n]);

  return {
    // State
    user,
    isAuthenticated,
    hasRefreshToken,
    isLoading,

    // Actions
    login,
    logout,
  };
}
